/**
 * Доменные типы раздела «Что нового» (информационные баннеры обновлений).
 *
 * Зеркало DTO роутов `/api/v1/announcements/*` и `/api/v1/admin/announcements/*`,
 * snake_case 1:1 с backend serde. Пользователь видит опубликованные карточки
 * (`Announcement`), админ — черновики и опубликованные с метаданными рассылки
 * (`AdminAnnouncement`).
 */

import type { ComponentType, SVGProps } from 'react';
import type { OsnovaTheme } from '@shared/theme/tokens';
import { IconSparkles, IconTrendingUp, IconWrench } from '@shared/ui/icons';
import { agentLabel } from '@shared/agents/labels';

/** Тип обновления: новая функция, улучшение существующей, исправление. */
export type AnnouncementKind = 'new' | 'improvement' | 'fix';

/**
 * Приводит произвольную строку (например, `kind` из WS-события, где по контракту
 * это `string`) к валидному `AnnouncementKind`. Неизвестное значение — «new»,
 * чтобы карточка всегда отрисовалась с осмысленным бейджем.
 */
export function normalizeKind(value: string | null | undefined): AnnouncementKind {
  return value === 'improvement' || value === 'fix' ? value : 'new';
}

/** Статус карточки в админке: черновик (не разослан) / опубликован. */
export type AnnouncementStatus = 'draft' | 'published';

/** Карточка обновления в ленте пользователя. `published_at` есть всегда для
 *  выданных пользователю карточек; `read_at` — момент прочтения (null — не
 *  прочитано, влияет на бейдж непрочитанных).
 *
 *  `popup_seen_at` — момент, когда этому пользователю всплывающая модалка
 *  анонса была ПОКАЗАНА (null — ещё не показывалась; ровно такие карточки
 *  формируют очередь всплывашек, гарантия «показать 1 раз» держится сервером).
 *  `cleared_at` — момент, когда пользователь убрал карточку из своей ленты
 *  (null — в ленте; убранные из всплывашек исключаем). */
export interface Announcement {
  id: string;
  agent_code: string | null;
  kind: AnnouncementKind;
  title: string;
  body: string;
  published_at: string | null;
  read_at: string | null;
  popup_seen_at: string | null;
  cleared_at: string | null;
}

/** Карточка обновления в админ-ревью: то же + метаданные рассылки. */
export interface AdminAnnouncement {
  id: string;
  agent_code: string | null;
  kind: AnnouncementKind;
  title: string;
  body: string;
  source_ref: string | null;
  status: AnnouncementStatus;
  published_at: string | null;
  created_at: string;
  expires_at: string | null;
}

/**
 * Смердженный PR — кандидат на генерацию карточки обновления. Backend уже
 * подсказал тип (`suggested_kind`) и агента (`suggested_agent_code`, `null` —
 * общесистемное), но финальное решение за админом. Зеркало DTO
 * `GET /admin/announcements/pr-candidates`.
 */
export interface PrCandidate {
  number: number;
  title: string;
  suggested_kind: AnnouncementKind;
  suggested_agent_code: string | null;
  merged_at: string | null;
  /** GitHub-логин автора PR. `null` — backend не передал автора. */
  author: string | null;
}

/** Уже опубликованный PR — вышел в опубликованной карточке. Зеркало DTO
 *  `GET /admin/announcements/pr-published`. Повторно анонсировать нельзя. */
export interface PublishedPr {
  number: number;
  title: string;
  author: string | null;
  merged_at: string | null;
  announcement_id: string;
  announcement_title: string;
  published_at: string | null;
}

/** Режим генерации: карточка на каждый PR / объединить выбранные в одну. */
export type AnnouncementMergeMode = 'per_pr' | 'combined';

/**
 * Тело `POST /admin/announcements/generate`. `scope` — целевой агент карточки:
 * `auto` (определить из PR), `system` (общесистемное) либо конкретный agent_code.
 * При `merge_mode='per_pr'` агент берётся из каждого PR на бэкенде, поэтому
 * `scope` передаём как `auto`.
 */
export interface GenerateAnnouncementsBody {
  pr_numbers: number[];
  scope: string;
  merge_mode: AnnouncementMergeMode;
}

/** Статус фоновой джобы генерации черновиков. */
export type GenBatchState = 'queued' | 'running' | 'done' | 'error';

/**
 * Ответ `GET /admin/announcements/generate/{batch_id}`. На терминальном
 * статусе (`done`/`error`) поллинг останавливается; `created_count`/
 * `skipped_count` наполняются к `done`, `error` — текст на `error`.
 */
export interface GenBatchStatus {
  status: GenBatchState;
  created_count: number;
  skipped_count: number;
  error: string | null;
}

/** Тон бейджа `@shared/ui/Badge` для типа обновления. */
export type AnnouncementTone = 'info' | 'success' | 'warning';

/** Иконка типа обновления (совместима с обёрткой `Svg` из `@shared/ui/icons`). */
type IconComponent = ComponentType<SVGProps<SVGSVGElement> & { size?: number }>;

export interface AnnouncementKindMeta {
  label: string;
  tone: AnnouncementTone;
  /** Акцентный цвет из темы — для полосы/иконки карточки. */
  color: (theme: OsnovaTheme) => string;
  Icon: IconComponent;
}

/**
 * Описание типа обновления: подпись, тон бейджа, акцентный цвет и иконка.
 *  new → info/«Новое», improvement → success/«Улучшение», fix → warning/«Исправление».
 */
export function kindMeta(kind: AnnouncementKind): AnnouncementKindMeta {
  switch (kind) {
    case 'new':
      return {
        label: 'Новое',
        tone: 'info',
        color: (t) => t.colors.status.info,
        Icon: IconSparkles,
      };
    case 'improvement':
      return {
        label: 'Улучшение',
        tone: 'success',
        color: (t) => t.colors.status.success,
        Icon: IconTrendingUp,
      };
    case 'fix':
      return {
        label: 'Исправление',
        tone: 'warning',
        color: (t) => t.colors.status.warning,
        Icon: IconWrench,
      };
  }
}

/**
 * Подпись агента, к которому относится обновление. `null` (общесистемное) →
 * «Система». Переиспользует единый справочник `@shared/agents/labels`.
 */
export function announcementAgentLabel(code: string | null | undefined): string {
  return agentLabel(code);
}

/**
 * Относительное время публикации: «только что» / «N мин назад» / «N ч назад» /
 * «N дн назад», дальше — абсолютная дата (ru-RU). `null`/невалидное — прочерк.
 */
export function formatPublishedAt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const ms = d.getTime();
  if (Number.isNaN(ms)) return '—';
  const diffSec = Math.round((Date.now() - ms) / 1000);
  if (diffSec < 45) return 'только что';
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin} мин назад`;
  const diffHour = Math.round(diffMin / 60);
  if (diffHour < 24) return `${diffHour} ч назад`;
  const diffDay = Math.round(diffHour / 24);
  if (diffDay < 7) return `${diffDay} дн назад`;
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
